import { Link } from "react-router-dom";
import { Button } from "@learn-me/ui";
import { motion } from "framer-motion";
import { FadeIn } from "@/components/animated";
import { ArrowRight, Play, Star } from "lucide-react";

// Student avatars for social proof
const studentAvatars = [
  "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=80&h=80&fit=crop&crop=face",
  "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=80&h=80&fit=crop&crop=face",
  "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=80&h=80&fit=crop&crop=face",
  "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=80&h=80&fit=crop&crop=face",
  "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=80&h=80&fit=crop&crop=face",
];

export function HeroSection() {
  return (
    <section className="relative min-h-[90vh] flex items-center overflow-hidden">
      {/* ===== MESH GRADIENT BACKGROUND ===== */}
      <div className="absolute inset-0">
        {/* Base gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-blue-50/80 to-indigo-100/60" />

        {/* Mesh gradient blobs */}
        <div
          className="absolute top-0 -left-4 w-96 h-96 bg-purple-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse-slow"
          style={{ animationDelay: "0s" }}
        />
        <div
          className="absolute top-0 -right-4 w-96 h-96 bg-cyan-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse-slow"
          style={{ animationDelay: "2s" }}
        />
        <div
          className="absolute -bottom-8 left-20 w-96 h-96 bg-pink-200 rounded-full mix-blend-multiply filter blur-3xl opacity-25 animate-pulse-slow"
          style={{ animationDelay: "4s" }}
        />
        <div
          className="absolute bottom-0 right-20 w-80 h-80 bg-amber-100 rounded-full mix-blend-multiply filter blur-3xl opacity-40 animate-pulse-slow"
          style={{ animationDelay: "1s" }}
        />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-blue-100 rounded-full mix-blend-multiply filter blur-3xl opacity-20" />

        {/* Subtle noise texture overlay */}
        <div
          className="absolute inset-0 opacity-[0.015]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
          }}
        />
      </div>

      {/* ===== MAIN CONTENT ===== */}
      <div className="container relative z-10 py-20">
        <div className="max-w-4xl mx-auto text-center">
          {/* Social proof badge with avatars */}
          <FadeIn direction="up" delay={0.1}>
            <div className="flex justify-center mb-10">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
                className="inline-flex items-center gap-4 px-5 py-2.5 bg-white/70 backdrop-blur-xl rounded-full shadow-lg shadow-black/[0.03] border border-white/80"
              >
                {/* Stacked avatars */}
                <div className="flex -space-x-2.5">
                  {studentAvatars.map((avatar, i) => (
                    <img
                      key={i}
                      src={avatar}
                      alt=""
                      className="w-8 h-8 rounded-full border-2 border-white object-cover ring-1 ring-black/5"
                    />
                  ))}
                  <div className="w-8 h-8 rounded-full border-2 border-white bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center text-white text-[10px] font-bold shadow-sm">
                    +9k
                  </div>
                </div>

                <div className="h-5 w-px bg-secondary-200" />

                {/* Rating */}
                <div className="flex items-center gap-1.5">
                  <div className="flex">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className="w-3.5 h-3.5 text-amber-400 fill-amber-400"
                      />
                    ))}
                  </div>
                  <span className="text-sm font-semibold text-secondary-800">
                    4.9
                  </span>
                  <span className="text-sm text-secondary-500">(2.8k)</span>
                </div>
              </motion.div>
            </div>
          </FadeIn>

          {/* Main headline */}
          <FadeIn direction="up" delay={0.2}>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-secondary-900 mb-6">
              Tecnología y formación que
              <span className="block mt-2 bg-gradient-to-r from-primary-600 via-blue-600 to-indigo-600 bg-clip-text text-transparent">
                se adaptan a su organización
              </span>
            </h1>
          </FadeIn>

          {/* Subheadline */}
          <FadeIn direction="up" delay={0.3}>
            <p className="text-lg sm:text-xl text-secondary-600 mb-10 max-w-2xl mx-auto leading-relaxed">
              Creamos soluciones digitales personalizadas, rutas formativas y
              programas de capacitación diseñados alrededor de las necesidades
              de cada organización.
            </p>
          </FadeIn>

          {/* CTA Buttons */}
          <FadeIn direction="up" delay={0.4}>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
              <Link to="/empresas">
                <motion.div
                  whileHover={{ scale: 1.02, y: -1 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Button
                    size="lg"
                    className="w-full sm:w-auto px-7 py-6 text-base font-semibold bg-secondary-900 hover:bg-secondary-800 text-white rounded-xl shadow-lg shadow-secondary-900/20"
                  >
                    Soluciones para empresas
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </motion.div>
              </Link>

              <Link to="/programas">
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Button
                    variant="outline"
                    size="lg"
                    className="w-full sm:w-auto px-7 py-6 text-base font-semibold bg-white/60 backdrop-blur-sm border border-secondary-200/80 hover:bg-white hover:border-secondary-300 text-secondary-700 rounded-xl"
                  >
                    Programas y capacitaciones
                  </Button>
                </motion.div>
              </Link>

              <Link to="/courses">
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Button
                    variant="outline"
                    size="lg"
                    className="w-full sm:w-auto px-7 py-6 text-base font-semibold bg-white/60 backdrop-blur-sm border border-secondary-200/80 hover:bg-white hover:border-secondary-300 text-secondary-700 rounded-xl"
                  >
                    <Play className="mr-2 h-4 w-4" />
                    Aprendizaje en línea
                  </Button>
                </motion.div>
              </Link>
            </div>
          </FadeIn>

          {/* Trust indicators */}
          <FadeIn direction="up" delay={0.5}>
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-sm text-secondary-500">
              {[
                "Sin tarjeta de crédito",
                "Acceso inmediato",
                "Certificados incluidos",
              ].map((text, i) => (
                <div key={i} className="flex items-center gap-2">
                  <svg
                    className="w-4 h-4 text-emerald-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2.5}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  <span>{text}</span>
                </div>
              ))}
            </div>
          </FadeIn>
        </div>
      </div>

      {/* Bottom fade to white */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white to-transparent pointer-events-none" />
    </section>
  );
}
